import { useEffect, useState } from "react";
import { ChevronRight, Paperclip } from "lucide-react";
import { api, type MessageDetail } from "../api";
import { MailRahmen } from "./MailRahmen";
import { datumLang } from "../lib/datum";
import { fehlerText } from "./ui/Toast";

const OFFEN_KEY = "maildeck-ursprung-offen";

/**
 * Die Nachricht, auf die geantwortet wird — im Composer einklappbar über dem
 * Text. Wer einen vorgeschriebenen Entwurf prüft, muss sehen, worauf er
 * eigentlich antwortet, ohne den Dialog zu verlassen.
 *
 * `vorgeschrieben`: Bei Entwürfen des Agenten startet sie aufgeklappt, sonst
 * gilt die zuletzt gewählte Stellung.
 */
export function Ursprung({ messageId, vorgeschrieben }: { messageId: number; vorgeschrieben: boolean }) {
  const [nachricht, setNachricht] = useState<MessageDetail | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState(() => {
    if (vorgeschrieben) return true;
    try { return localStorage.getItem(OFFEN_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    let ab = false;
    api.message(messageId).then((m) => { if (!ab) setNachricht(m); }, (err) => { if (!ab) setFehler(fehlerText(err)); });
    return () => { ab = true; };
  }, [messageId]);

  const umschalten = () => {
    setOffen((o) => {
      try { localStorage.setItem(OFFEN_KEY, o ? "0" : "1"); } catch { /* privater Modus */ }
      return !o;
    });
  };

  const absender = nachricht ? nachricht.from_name || nachricht.from_email : "";

  return (
    <section className="ursprung" data-offen={offen} aria-label="Ursprüngliche Nachricht">
      <button type="button" className="ursprung-kopf" aria-expanded={offen} onClick={umschalten}>
        <ChevronRight className="ursprung-pfeil" aria-hidden="true" />
        <span className="ursprung-titel">Ursprüngliche Nachricht</span>
        {nachricht && (
          <span className="ursprung-meta">
            <b>{absender}</b> · {datumLang(nachricht.date)}
            {nachricht.attachments.length > 0 && <> · <Paperclip aria-hidden="true" /> {nachricht.attachments.length}</>}
          </span>
        )}
      </button>
      {offen && (
        <div className="ursprung-inhalt">
          {fehler ? (
            <p className="hint" role="alert">Konnte nicht geladen werden: {fehler}</p>
          ) : !nachricht ? (
            <p className="hint" aria-busy="true">Lade …</p>
          ) : (
            <>
              {nachricht.summary && <p className="ursprung-kurz">{nachricht.summary}</p>}
              {nachricht.body_text?.trim() ? (
                <pre className="ursprung-text">{nachricht.body_text.trim()}</pre>
              ) : nachricht.body_html ? (
                <MailRahmen html={nachricht.body_html} bilderFrei={false} />
              ) : (
                <p className="hint">(kein Textinhalt)</p>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
